// Single source of truth for the Cellular Component (GO CC) terms used by
// the CC filter bar chart, the CC tree view, and the Protein info page filter.
//
// The full list is the EXHAUSTIVE descendant closure of GO:0005576
// "extracellular region" (is_a + part_of), from GO release 2026-05-19, with
// obsolete terms removed (notably GO:0005615 "extracellular space", merged
// into GO:0005576). See ccGoTerms.references.md.
//
// `parent` is the term's preferred parent GO id within this whitelist (null
// for the root), used to build the hierarchical tree view. `inChart: true`
// marks the curated, data-driven terms shown as bars in the CC bar chart.
// The Protein info page filter uses the FULL list below.
const CC_GO_TERMS = [
  { id: 'GO:0043509', label: 'Activin A complex', parent: 'GO:0048180' },
  { id: 'GO:0048183', label: 'Activin AB complex', parent: 'GO:0048180' },
  { id: 'GO:0043510', label: 'Activin B complex', parent: 'GO:0048180' },
  { id: 'GO:0048180', label: 'Activin complex', parent: 'GO:0140392' },
  { id: 'GO:0060111', label: 'Alae of collagen and cuticulin-based cuticle extracellular matrix', parent: 'GO:0060102' },
  { id: 'GO:0032311', label: 'Angiogenin-PRI complex', parent: 'GO:0140392' },
  { id: 'GO:0060108', label: 'Annular furrow extracellular matrix', parent: 'GO:0060106' },
  { id: 'GO:0060107', label: 'Annuli extracellular matrix', parent: 'GO:0060106' },
  { id: 'GO:0032579', label: 'Apical lamina of hyaline layer', parent: 'GO:0033166' },
  { id: 'GO:0048046', label: 'Apoplast', parent: 'GO:0005576' },
  { id: 'GO:0097189', label: 'Apoptotic body', parent: 'GO:1903561' },
  { id: 'GO:0005619', label: 'Ascospore wall', parent: 'GO:0009277' },
  { id: 'GO:0097515', label: 'Asexual spore wall', parent: 'GO:0031160' },
  { id: 'GO:0097311', label: 'Bacterial biofilm matrix', parent: 'GO:0062039' },
  { id: 'GO:0097313', label: 'Bacterial biofilm matrix surface', parent: 'GO:0097311' },
  { id: 'GO:0097691', label: 'Bacterial extracellular vesicle', parent: 'GO:1903561' },
  { id: 'GO:0061701', label: 'Bacterial outer membrane vesicle', parent: 'GO:1903561' },
  { id: 'GO:1990063', label: 'Bam protein complex', parent: 'GO:0140392' },
  { id: 'GO:0060110', label: 'Basal layer of collagen and cuticulin-based cuticle extracellular matrix', parent: 'GO:0060102' },
  { id: 'GO:0005604', label: 'Basement membrane', parent: 'GO:0031012', inChart: true },
  { id: 'GO:0140086', label: 'Basement membrane/interstitial matrix interface', parent: 'GO:0031012' },
  { id: 'GO:0062039', label: 'Biofilm matrix', parent: 'GO:0140047' },
  { id: 'GO:0072562', label: 'Blood microparticle', parent: 'GO:0005576', inChart: true },
  { id: 'GO:0031395', label: 'Bursicon neuropeptide hormone complex', parent: 'GO:0140392' },
  { id: 'GO:0042603', label: 'Capsule', parent: 'GO:0030112' },
  { id: 'GO:0048226', label: 'Casparian strip', parent: 'GO:0009531' },
  { id: 'GO:0009279', label: 'Cell outer membrane', parent: 'GO:0030312' },
  { id: 'GO:0043188', label: 'Cell septum edging', parent: 'GO:0009277' },
  { id: 'GO:0005618', label: 'Cell wall', parent: 'GO:0030312' },
  { id: 'GO:0030287', label: 'Cell wall-bounded periplasmic space', parent: 'GO:0042597' },
  { id: 'GO:0071597', label: 'Cellular birth scar', parent: 'GO:0009277' },
  { id: 'GO:0005621', label: 'Cellular bud scar', parent: 'GO:0009277' },
  { id: 'GO:0009549', label: 'Cellulose microfibril', parent: 'GO:0009505' },
  { id: 'GO:0043263', label: 'Cellulosome', parent: 'GO:0043264' },
  { id: 'GO:0140154', label: 'Chicken-wire-like collagen network', parent: 'GO:0140143' },
  { id: 'GO:0062129', label: 'Chitin-based extracellular matrix', parent: 'GO:0140047' },
  { id: 'GO:0005631', label: 'Chitosan layer of spore wall', parent: 'GO:0005619' },
  { id: 'GO:0070825', label: 'Chrorion micropyle', parent: 'GO:0042600' },
  { id: 'GO:0042627', label: 'Chylomicron', parent: 'GO:0034358' },
  { id: 'GO:0034360', label: 'Chylomicron remnant', parent: 'GO:0042627' },
  { id: 'GO:0005601', label: 'Classical-complement-pathway C3/C5 convertase complex', parent: 'GO:0140392' },
  { id: 'GO:0097059', label: 'CNTFR-CLCF1 complex', parent: 'GO:0140392' },
  { id: 'GO:0098648', label: 'Collagen anchoring fibril', parent: 'GO:0140086' },
  { id: 'GO:0098647', label: 'Collagen beaded filament', parent: 'GO:0140086' },
  { id: 'GO:0005584', label: 'Collagen type I trimer', parent: 'GO:0005583' },
  { id: 'GO:0005585', label: 'Collagen type II trimer', parent: 'GO:0005583' },
  { id: 'GO:0005586', label: 'Collagen type III trimer', parent: 'GO:0005583' },
  { id: 'GO:0005587', label: 'Collagen type IV trimer', parent: 'GO:0098642' },
  { id: 'GO:0005594', label: 'Collagen type IX trimer', parent: 'GO:0005593' },
  { id: 'GO:0005588', label: 'Collagen type V trimer', parent: 'GO:0005583' },
  { id: 'GO:0005589', label: 'Collagen type VI trimer', parent: 'GO:0140392' },
  { id: 'GO:0005590', label: 'Collagen type VII trimer', parent: 'GO:0140392' },
  { id: 'GO:0005591', label: 'Collagen type VIII trimer', parent: 'GO:0098642' },
  { id: 'GO:0005599', label: 'Collagen type X trimer', parent: 'GO:0098642' },
  { id: 'GO:0005592', label: 'Collagen type XI trimer', parent: 'GO:0005583' },
  { id: 'GO:0005595', label: 'Collagen type XII trimer', parent: 'GO:0005593' },
  { id: 'GO:0005596', label: 'Collagen type XIV trimer', parent: 'GO:0005593' },
  { id: 'GO:1990318', label: 'Collagen type XIX trimer', parent: 'GO:0140392' },
  { id: 'GO:0005582', label: 'Collagen type XV trimer', parent: 'GO:0140156' },
  { id: 'GO:0005597', label: 'Collagen type XVI trimer', parent: 'GO:0005593' },
  { id: 'GO:0030938', label: 'Collagen type XVIII trimer', parent: 'GO:0140156' },
  { id: 'GO:1990319', label: 'Collagen type XX trimer', parent: 'GO:0005593' },
  { id: 'GO:1990320', label: 'Collagen type XXI trimer', parent: 'GO:0005593' },
  { id: 'GO:1990321', label: 'Collagen type XXII trimer', parent: 'GO:0005593' },
  { id: 'GO:1990323', label: 'Collagen type XXIV trimer', parent: 'GO:0005583' },
  { id: 'GO:1990324', label: 'Collagen type XXVI trimer', parent: 'GO:0140392' },
  { id: 'GO:1990325', label: 'Collagen type XXVII trimer', parent: 'GO:0005583' },
  { id: 'GO:1990326', label: 'Collagen type XXVIII trimer', parent: 'GO:0140392' },
  { id: 'GO:0140143', label: 'Collagenous component of basement membrane', parent: 'GO:0030312' },
  { id: 'GO:0140152', label: 'Collagenous component of interstitial matrix', parent: 'GO:0030312' },
  { id: 'GO:0043674', label: 'Columella', parent: 'GO:0043673' },
  { id: 'GO:0005602', label: 'Complement component C1 complex', parent: 'GO:0140392' },
  { id: 'GO:0062167', label: 'Complement component C1q complex', parent: 'GO:0140392' },
  { id: 'GO:0097219', label: 'Compound sieve plate', parent: 'GO:0097218' },
  { id: 'GO:0090658', label: 'Cone matrix sheath', parent: 'GO:0033165' },
  { id: 'GO:0060106', label: 'Cortical layer of collagen and cuticulin-based cuticle extracellular matrix', parent: 'GO:0060102' },
  { id: 'GO:0097058', label: 'CRLF-CLCF1 complex', parent: 'GO:0140392' },
  { id: 'GO:0098774', label: 'Curli', parent: 'GO:0097311' },
  { id: 'GO:0062155', label: 'Curli secretion complex', parent: 'GO:0140392' },
  { id: 'GO:0060102', label: 'Cuticular extracellular matrix', parent: 'GO:0140047' },
  { id: 'GO:0097570', label: 'Cyst wall', parent: 'GO:0005618' },
  { id: 'GO:0097735', label: 'DIM/DIP cell wall layer', parent: 'GO:0009275' },
  { id: 'GO:0071750', label: 'Dimeric IgA immunoglobulin complex', parent: 'GO:0071749' },
  { id: 'GO:0034365', label: 'Discoidal high-density lipoprotein particle', parent: 'GO:0034364' },
  { id: 'GO:0005630', label: 'Dityrosine layer of spore wall', parent: 'GO:0005619' },
  { id: 'GO:0043669', label: 'Ectexine', parent: 'GO:0043668' },
  { id: 'GO:0042600', label: 'Egg chorion', parent: 'GO:0030312' },
  { id: 'GO:0035805', label: 'Egg coat', parent: 'GO:0140047' },
  { id: 'GO:0071953', label: 'Elastic fiber', parent: 'GO:0140149' },
  { id: 'GO:0140400', label: 'Embryonic sheath', parent: 'GO:0140047' },
  { id: 'GO:1990971', label: 'EMILIN complex', parent: 'GO:0140392' },
  { id: 'GO:0043671', label: 'Endexine', parent: 'GO:0043668' },
  { id: 'GO:0043593', label: 'Endospore coat', parent: 'GO:0043591' },
  { id: 'GO:0043595', label: 'Endospore cortex', parent: 'GO:0043591' },
  { id: 'GO:0043591', label: 'Endospore external encapsulating structure', parent: 'GO:0031160' },
  { id: 'GO:0072563', label: 'Endothelial microparticle', parent: 'GO:0072562' },
  { id: 'GO:0060105', label: 'Epicuticle of collagen and cuticulin-based cuticle extracellular matrix', parent: 'GO:0060102' },
  { id: 'GO:0098875', label: 'Epididymosome', parent: 'GO:1990742' },
  { id: 'GO:0043668', label: 'Exine', parent: 'GO:0043667' },
  { id: 'GO:0160014', label: 'Exopher', parent: 'GO:1903561' },
  { id: 'GO:0043592', label: 'Exosporium', parent: 'GO:0005618' },
  { id: 'GO:0030312', label: 'External encapsulating structure', parent: 'GO:0005576' },
  { id: 'GO:0031240', label: 'External side of cell outer membrane', parent: 'GO:0009279' },
  { id: 'GO:0010339', label: 'External side of cell wall', parent: 'GO:0005618' },
  { id: 'GO:0070263', label: 'External side of fungal-type cell wall', parent: 'GO:0009277' },
  { id: 'GO:0098568', label: 'External side of mycolate outer membrane', parent: 'GO:0031240' },
  { id: 'GO:0070062', label: 'Extracellular exosome', parent: 'GO:1903561', inChart: true },
  { id: 'GO:1990563', label: 'Extracellular exosome complex', parent: 'GO:0140392' },
  { id: 'GO:0010367', label: 'Extracellular isoamylase complex', parent: 'GO:0140392' },
  { id: 'GO:0031012', label: 'Extracellular matrix', parent: 'GO:0030312', inChart: true },
  { id: 'GO:0098965', label: 'Extracellular matrix of synaptic cleft', parent: 'GO:0140047' },
  { id: 'GO:0065010', label: 'Extracellular membrane-bounded organelle', parent: 'GO:0043230' },
  { id: 'GO:0043264', label: 'Extracellular membraneless organelle', parent: 'GO:0043230' },
  { id: 'GO:0043230', label: 'Extracellular organelle', parent: 'GO:0005576' },
  { id: 'GO:0140392', label: 'Extracellular protein-containing complex', parent: 'GO:0005576' },
  { id: 'GO:0005576', label: 'Extracellular region', parent: null, inChart: true },
  { id: 'GO:1903561', label: 'Extracellular vesicle', parent: 'GO:0065010' },
  { id: 'GO:0085036', label: 'Extrahaustorial matrix', parent: 'GO:0005576' },
  { id: 'GO:0043245', label: 'Extraorganismal space', parent: 'GO:0005576' },
  { id: 'GO:0031244', label: 'Extrinsic component of cell outer membrane', parent: 'GO:0009279' },
  { id: 'GO:0031242', label: 'Extrinsic component of external side of cell outer membrane', parent: 'GO:0031240' },
  { id: 'GO:0036421', label: 'Extrinsic component of external side of mycolate outer membrane', parent: 'GO:0031242' },
  { id: 'GO:0036420', label: 'Extrinsic component of mycolate outer membrane', parent: 'GO:0031244' },
  { id: 'GO:0031245', label: 'Extrinsic component of periplasmic side of cell outer membrane', parent: 'GO:0031241' },
  { id: 'GO:0140153', label: 'FACIT collagen complex', parent: 'GO:0140152' },
  { id: 'GO:0005593', label: 'FACIT collagen trimer', parent: 'GO:0140392' },
  { id: 'GO:0035183', label: 'Female germline ring canal inner rim', parent: 'GO:0005576' },
  { id: 'GO:0035182', label: 'Female germline ring canal outer rim', parent: 'GO:0005576' },
  { id: 'GO:0060387', label: 'Fertilization envelope', parent: 'GO:0035805' },
  { id: 'GO:0098643', label: 'Fibrillar collagen complex', parent: 'GO:0140152' },
  { id: 'GO:0005583', label: 'Fibrillar collagen trimer', parent: 'GO:0140392' },
  { id: 'GO:0005577', label: 'Fibrinogen complex', parent: 'GO:0140392' },
  { id: 'GO:0061800', label: 'Fibronectin fibril', parent: 'GO:0140149' },
  { id: 'GO:0043680', label: 'Filiform apparatus', parent: 'GO:0005618' },
  { id: 'GO:0016914', label: 'Follicle-stimulating hormone complex', parent: 'GO:0061696' },
  { id: 'GO:0043670', label: 'Foot layer', parent: 'GO:0043669' },
  { id: 'GO:0062040', label: 'Fungal biofilm matrix', parent: 'GO:0062039' },
  { id: 'GO:0009277', label: 'Fungal-type cell wall', parent: 'GO:0005618' },
  { id: 'GO:0140148', label: 'Gel phase of basement membrane', parent: 'GO:0030312' },
  { id: 'GO:0140150', label: 'Gel phase of interstitial matrix', parent: 'GO:0030312' },
  { id: 'GO:0030112', label: 'Glycocalyx', parent: 'GO:0030312' },
  { id: 'GO:0048222', label: 'Glycoprotein network', parent: 'GO:0009530' },
  { id: 'GO:0009276', label: 'Gram-negative-bacterium-type cell wall', parent: 'GO:0009274' },
  { id: 'GO:0009275', label: 'Gram-positive-bacterium-type cell wall', parent: 'GO:0009274' },
  { id: 'GO:0048223', label: 'Hemicellulose network', parent: 'GO:0009530' },
  { id: 'GO:0098646', label: 'Hexagonal collagen network', parent: 'GO:0140152' },
  { id: 'GO:0071757', label: 'Hexameric IgM immunoglobulin complex', parent: 'GO:0071754' },
  { id: 'GO:0034364', label: 'High-density lipoprotein particle', parent: 'GO:0034358', inChart: true },
  { id: 'GO:0033166', label: 'Hyaline layer', parent: 'GO:0140047' },
  { id: 'GO:0036117', label: 'Hyaluranon cable', parent: 'GO:0005576' },
  { id: 'GO:0030446', label: 'Hyphal cell wall', parent: 'GO:0009277' },
  { id: 'GO:0071746', label: 'IgA immunoglobulin complex, circulating', parent: 'GO:0042571' },
  { id: 'GO:0071739', label: 'IgD immunoglobulin complex, circulating', parent: 'GO:0042571' },
  { id: 'GO:0071743', label: 'IgE immunoglobulin complex, circulating', parent: 'GO:0042571' },
  { id: 'GO:0071736', label: 'IgG immunoglobulin complex, circulating', parent: 'GO:0042571' },
  { id: 'GO:0071754', label: 'IgM immunoglobulin complex, circulating', parent: 'GO:0042571' },
  { id: 'GO:0042571', label: 'Immunoglobulin complex, circulating', parent: 'GO:0140392', inChart: true },
  { id: 'GO:0043512', label: 'Inhibin A complex', parent: 'GO:0043511' },
  { id: 'GO:0043513', label: 'Inhibin B complex', parent: 'GO:0043511' },
  { id: 'GO:0043511', label: 'Inhibin complex', parent: 'GO:0140392' },
  { id: 'GO:0005632', label: 'Inner layer of spore wall', parent: 'GO:0005619' },
  { id: 'GO:0070702', label: 'Inner mucus layer', parent: 'GO:0070701' },
  { id: 'GO:0042568', label: 'Insulin-like growth factor binary complex', parent: 'GO:0016942' },
  { id: 'GO:0016942', label: 'Insulin-like growth factor binding protein complex', parent: 'GO:0140392' },
  { id: 'GO:0042567', label: 'Insulin-like growth factor ternary complex', parent: 'GO:0016942' },
  { id: 'GO:0043514', label: 'Interleukin-12 complex', parent: 'GO:0140392' },
  { id: 'GO:0070743', label: 'Interleukin-23 complex', parent: 'GO:0140392' },
  { id: 'GO:0070744', label: 'Interleukin-27 complex', parent: 'GO:0140392' },
  { id: 'GO:0070745', label: 'Interleukin-35 complex', parent: 'GO:0140392' },
  { id: 'GO:0034363', label: 'Intermediate-density lipoprotein particle', parent: 'GO:0034385' },
  { id: 'GO:0033165', label: 'Interphotoreceptor matrix', parent: 'GO:0140047' },
  { id: 'GO:0005614', label: 'Interstitial matrix', parent: 'GO:0031012' },
  { id: 'GO:0043678', label: 'Intine', parent: 'GO:0043667' },
  { id: 'GO:1990916', label: 'Isp3 layer of spore wall', parent: 'GO:0005619' },
  { id: 'GO:0140349', label: 'Laminin network', parent: 'GO:0140144' },
  { id: 'GO:0043256', label: 'Laminin trimer', parent: 'GO:0140392' },
  { id: 'GO:0005606', label: 'Laminin-111 trimer', parent: 'GO:0043256' },
  { id: 'GO:0005608', label: 'Laminin-121 trimer', parent: 'GO:0043256' },
  { id: 'GO:0005607', label: 'Laminin-211 trimer', parent: 'GO:0043256' },
  { id: 'GO:0043261', label: 'Laminin-213 trimer', parent: 'GO:0043256' },
  { id: 'GO:0005609', label: 'Laminin-221 trimer', parent: 'GO:0043256' },
  { id: 'GO:0005611', label: 'Laminin-311 trimer', parent: 'GO:0043256' },
  { id: 'GO:0005612', label: 'Laminin-321 trimer', parent: 'GO:0043256' },
  { id: 'GO:0005610', label: 'Laminin-332 trimer', parent: 'GO:0043256' },
  { id: 'GO:0061801', label: 'Laminin-3B32 trimer', parent: 'GO:0043256' },
  { id: 'GO:0043257', label: 'Laminin-411 trimer', parent: 'GO:0043256' },
  { id: 'GO:0043258', label: 'Laminin-421 trimer', parent: 'GO:0043256' },
  { id: 'GO:1990338', label: 'Laminin-423 trimer', parent: 'GO:0043256' },
  { id: 'GO:0043259', label: 'Laminin-511 trimer', parent: 'GO:0043256' },
  { id: 'GO:0043260', label: 'Laminin-521 trimer', parent: 'GO:0043256' },
  { id: 'GO:1990339', label: 'Laminin-522 trimer', parent: 'GO:0043256' },
  { id: 'GO:1990340', label: 'Laminin-523 trimer', parent: 'GO:0043256' },
  { id: 'GO:0005616', label: 'Larval serum protein complex', parent: 'GO:0140392' },
  { id: 'GO:0048224', label: 'Lignin network', parent: 'GO:0009531' },
  { id: 'GO:0034362', label: 'Low-density lipoprotein particle', parent: 'GO:0034358' },
  { id: 'GO:0120216', label: 'Matrilin complex', parent: 'GO:0140392' },
  { id: 'GO:0034359', label: 'Mature chylomicron', parent: 'GO:0042627' },
  { id: 'GO:0060109', label: 'Medial layer of collagen and cuticulin-based cuticle extracellular matrix', parent: 'GO:0060102' },
  { id: 'GO:0001527', label: 'Microfibril', parent: 'GO:0071953' },
  { id: 'GO:0090641', label: 'Microsporidian-type endospore', parent: 'GO:0031160' },
  { id: 'GO:0090642', label: 'Microsporidian-type exospore', parent: 'GO:0031160' },
  { id: 'GO:1990742', label: 'Microvesicle', parent: 'GO:1903561' },
  { id: 'GO:0009519', label: 'Middle lamella', parent: 'GO:0140047' },
  { id: 'GO:0071748', label: 'Monomeric IgA immunoglobulin complex', parent: 'GO:0071746' },
  { id: 'GO:0070701', label: 'Mucus layer', parent: 'GO:0005576' },
  { id: 'GO:1990972', label: 'Multimerin complex', parent: 'GO:0140392' },
  { id: 'GO:0140156', label: 'Multiplexin collagen trimer', parent: 'GO:0140392' },
  { id: 'GO:0036407', label: 'Mycolate outer membrane', parent: 'GO:0009279' },
  { id: 'GO:0098642', label: 'Network-forming collagen trimer', parent: 'GO:0140392' },
  { id: 'GO:0140644', label: 'Neutrophil extracellular trap', parent: 'GO:0043264' },
  { id: 'GO:0043672', label: 'Nexine', parent: 'GO:0043668' },
  { id: 'GO:0140144', label: 'Non-collagenous component of basement membrane', parent: 'GO:0030312' },
  { id: 'GO:0140149', label: 'Non-collagenous component of interstitial matrix', parent: 'GO:0030312' },
  { id: 'GO:1990377', label: 'Organomineral extracellular matrix', parent: 'GO:0140047' },
  { id: 'GO:0043594', label: 'Outer endospore membrane', parent: 'GO:0043591' },
  { id: 'GO:0030288', label: 'Outer membrane-bounded periplasmic space', parent: 'GO:0042597' },
  { id: 'GO:0070703', label: 'Outer mucus layer', parent: 'GO:0070701' },
  { id: 'GO:0048217', label: 'Pectic matrix', parent: 'GO:0009530' },
  { id: 'GO:0071756', label: 'Pentameric IgM immunoglobulin complex', parent: 'GO:0071754' },
  { id: 'GO:0009274', label: 'Peptidoglycan-based cell wall', parent: 'GO:0005618' },
  { id: 'GO:1990073', label: 'Perforation plate', parent: 'GO:0005618' },
  { id: 'GO:0072534', label: 'Perineuronal net', parent: 'GO:0098966' },
  { id: 'GO:0055040', label: 'Periplasmic flagellum', parent: 'GO:0042597' },
  { id: 'GO:0031241', label: 'Periplasmic side of cell outer membrane', parent: 'GO:0009279' },
  { id: 'GO:0042597', label: 'Periplasmic space', parent: 'GO:0005576' },
  { id: 'GO:0098966', label: 'Perisynaptic extracellular matrix', parent: 'GO:0099535' },
  { id: 'GO:0099544', label: 'Perisynaptic space', parent: 'GO:0005576' },
  { id: 'GO:0098595', label: 'Perivitelline space', parent: 'GO:0005576' },
  { id: 'GO:0061696', label: 'Pituitary gonadotropin complex', parent: 'GO:0140392' },
  { id: 'GO:0009505', label: 'Plant-type cell wall', parent: 'GO:0005618' },
  { id: 'GO:0034358', label: 'Plasma lipoprotein particle', parent: 'GO:0140392' },
  { id: 'GO:0120240', label: 'Platelet glycocalyx', parent: 'GO:0030112' },
  { id: 'GO:0062074', label: 'Pollen aperture', parent: 'GO:0043667' },
  { id: 'GO:0070505', label: 'Pollen coat', parent: 'GO:0140047' },
  { id: 'GO:0043667', label: 'Pollen wall', parent: 'GO:0030312' },
  { id: 'GO:0071749', label: 'Polymeric IgA immunoglobulin complex', parent: 'GO:0071746' },
  { id: 'GO:0009530', label: 'Primary cell wall', parent: 'GO:0009505' },
  { id: 'GO:0071914', label: 'Prominosome', parent: 'GO:1903561' },
  { id: 'GO:0110157', label: 'Reelin complex', parent: 'GO:0140392' },
  { id: 'GO:0030115', label: 'S-layer', parent: 'GO:0030312' },
  { id: 'GO:0043675', label: 'Sculpture element', parent: 'GO:0043673' },
  { id: 'GO:0009531', label: 'Secondary cell wall', parent: 'GO:0009505' },
  { id: 'GO:0071752', label: 'Secretory dimeric IgA immunoglobulin complex', parent: 'GO:0071750' },
  { id: 'GO:0071751', label: 'Secretory IgA immunoglobulin complex', parent: 'GO:0071749' },
  { id: 'GO:0036240', label: 'Septal periplasm', parent: 'GO:0042597' },
  { id: 'GO:0043673', label: 'Sexine', parent: 'GO:0043668' },
  { id: 'GO:0097514', label: 'Sexual spore wall', parent: 'GO:0031160' },
  { id: 'GO:0097217', label: 'Sieve area', parent: 'GO:0009505' },
  { id: 'GO:0097218', label: 'Sieve plate', parent: 'GO:0009505' },
  { id: 'GO:0097220', label: 'Simple sieve plate', parent: 'GO:0097218' },
  { id: 'GO:0030114', label: 'Slime layer', parent: 'GO:0030112' },
  { id: 'GO:0140139', label: 'Solid phase of basement membrane', parent: 'GO:0030312' },
  { id: 'GO:0140151', label: 'Solid phase of interstitial matrix', parent: 'GO:0030312' },
  { id: 'GO:0140047', label: 'Specialized extracellular matrix', parent: 'GO:0031012' },
  { id: 'GO:0120238', label: 'Sperm glycocalyx', parent: 'GO:0030112' },
  { id: 'GO:0034366', label: 'Spherical high-density lipoprotein particle', parent: 'GO:0034364' },
  { id: 'GO:0031160', label: 'Spore wall', parent: 'GO:0005618' },
  { id: 'GO:0044570', label: 'Starch utilization system complex', parent: 'GO:0140392' },
  { id: 'GO:0120234', label: 'Stereocilium coat', parent: 'GO:0030112' },
  { id: 'GO:0048225', label: 'Suberin network', parent: 'GO:0009531' },
  { id: 'GO:0060104', label: 'Surface coat of collagen and cuticulin-based cuticle extracellular matrix', parent: 'GO:0060102' },
  { id: 'GO:0099535', label: 'Synapse-associated extracellular matrix', parent: 'GO:0140047' },
  { id: 'GO:0043083', label: 'Synaptic cleft', parent: 'GO:0005576' },
  { id: 'GO:1990562', label: 'Syndecan-syntenin-ALIX complex', parent: 'GO:1990563' },
  { id: 'GO:0043676', label: 'Tectum', parent: 'GO:0043673' },
  { id: 'GO:0090733', label: 'Tenascin complex', parent: 'GO:0140392' },
  { id: 'GO:1990341', label: 'Thrombospondin complex', parent: 'GO:0140392' },
  { id: 'GO:0099126', label: 'Transforming growth factor beta complex', parent: 'GO:0140392' },
  { id: 'GO:0055039', label: 'Trichocyst', parent: 'GO:0043264' },
  { id: 'GO:0034385', label: 'Triglyceride-rich plasma lipoprotein particle', parent: 'GO:0034358' },
  { id: 'GO:0085026', label: 'Tubovesicular membrane network', parent: 'GO:0043264' },
  { id: 'GO:0070645', label: 'Ubisch body', parent: 'GO:0140047' },
  { id: 'GO:0120239', label: 'Vascular endothelial glycocalyx', parent: 'GO:0030112' },
  { id: 'GO:0034361', label: 'Very-low-density lipoprotein particle', parent: 'GO:0034385' },
  { id: 'GO:0060388', label: 'Vitelline envelope', parent: 'GO:0035805' },
  { id: 'GO:0030445', label: 'Yeast-form cell wall', parent: 'GO:0009277' },
];

// Curated subset rendered in the CC bar chart on the main grid.
const CC_CHART_TERMS = CC_GO_TERMS.filter(t => t.inChart);

export default CC_GO_TERMS;
export { CC_GO_TERMS, CC_CHART_TERMS };
